import { useState, useEffect, useCallback, useMemo } from "react";
import { Plus, Trash2, Loader2, Wallet, List, CalendarDays, Repeat } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import api from "../../lib/api";
import ExpensesCalendar from "./ExpensesCalendar";

const fmtEUR = (n) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n || 0);
const today = () => new Date().toISOString().slice(0, 10);

const CADENCE_OPTIONS = [
  { value: "monthly",   label: "Mensual" },
  { value: "bimonthly", label: "Bimestral" },
  { value: "quarterly", label: "Trimestral" },
  { value: "biannual",  label: "Semestral" },
  { value: "annual",    label: "Anual" },
];

const EMPTY = {
  date: today(), amount: "", category: "other",
  supplier_name: "", notes: "", tax_amount: "", paid: true,
  is_recurring: false, recurring_cadence: "monthly", recurring_day: "",
};

export default function ExpensesList({ restaurantId, categories, catLabels, range, onChange }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [pendingDup, setPendingDup] = useState(null); // { payload, duplicate }
  const [view, setView] = useState(() => {
    try { return localStorage.getItem("mg.expenses.view") || "list"; }
    catch { return "list"; }
  });

  const onViewChange = (v) => {
    setView(v);
    try { localStorage.setItem("mg.expenses.view", v); } catch { /* ignore */ }
  };

  const load = useCallback(async () => {
    if (!restaurantId) return;
    setLoading(true);
    try {
      const r = await api.listExpenses(restaurantId, {
        from: range?.from, to: range?.to,
      });
      setRows(r || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [restaurantId, range]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.amount || !form.date) {
      toast.error("Fecha e importe son obligatorios");
      return;
    }
    if (form.is_recurring && !form.supplier_name?.trim()) {
      toast.error("Para marcar como recurrente necesitas indicar el proveedor");
      return;
    }
    const payload = {
      ...form,
      amount: Number(form.amount),
      tax_amount: form.tax_amount === "" ? null : Number(form.tax_amount),
      supplier_name: form.supplier_name || null,
      notes: form.notes || null,
      is_recurring: !!form.is_recurring,
      recurring_cadence: form.is_recurring ? form.recurring_cadence : null,
      recurring_day: form.is_recurring
        ? Number(form.recurring_day || (form.date ? form.date.slice(8, 10) : 1))
        : null,
    };
    setSaving(true);
    try {
      await api.createExpense(restaurantId, payload);
      toast.success("Gasto registrado");
      setDialogOpen(false);
      setForm(EMPTY);
      await load();
      onChange?.();
    } catch (err) {
      if (err?.code === "duplicate_expense") {
        setPendingDup({ payload, duplicate: err.duplicate });
      } else {
        toast.error(err.message || "Error al registrar");
      }
    } finally { setSaving(false); }
  };

  const confirmAddDuplicate = async () => {
    if (!pendingDup) return;
    const { payload } = pendingDup;
    setPendingDup(null);
    setSaving(true);
    try {
      await api.createExpense(restaurantId, payload, { force: true });
      toast.success("Gasto registrado");
      setDialogOpen(false);
      setForm(EMPTY);
      await load();
      onChange?.();
    } catch (err) {
      toast.error(err.message || "Error al registrar");
    } finally { setSaving(false); }
  };

  const handleDelete = async (exp) => {
    try {
      await api.deleteExpense(restaurantId, exp.expense_id);
      toast.success("Gasto eliminado");
      setConfirmDel(null);
      await load();
      onChange?.();
    } catch (err) { toast.error(err.message || "Error al eliminar"); }
  };

  const total = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);

  // Group rows by date for the calendar heatmap
  const byDate = useMemo(() => {
    const m = new Map();
    for (const r of rows) {
      if (!r.date) continue;
      const arr = m.get(r.date) || [];
      arr.push(r);
      m.set(r.date, arr);
    }
    return m;
  }, [rows]);

  return (
    <div className="space-y-4" data-testid="expenses-list">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-semibold">{rows.length} gasto{rows.length === 1 ? "" : "s"} · {fmtEUR(total)}</h3>
          <p className="text-xs text-muted-foreground">
            Rango {range?.from} → {range?.to}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-md border bg-card p-0.5" data-testid="expenses-view-toggle">
            <button
              type="button"
              onClick={() => onViewChange("list")}
              data-testid="expenses-view-list"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-sm transition ${
                view === "list"
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground hover:bg-gray-50"
              }`}
            >
              <List className="h-3.5 w-3.5" /> Lista
            </button>
            <button
              type="button"
              onClick={() => onViewChange("calendar")}
              data-testid="expenses-view-calendar"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-sm transition ${
                view === "calendar"
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground hover:bg-gray-50"
              }`}
            >
              <CalendarDays className="h-3.5 w-3.5" /> Calendario
            </button>
          </div>
          <Button onClick={() => { setForm(EMPTY); setDialogOpen(true); }} data-testid="new-expense-btn">
            <Plus className="h-4 w-4 mr-1.5" /> Nuevo gasto
          </Button>
        </div>
      </div>

      {view === "calendar" ? (
        loading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : (
          <ExpensesCalendar
            byDate={byDate}
            range={range}
            catLabels={catLabels}
            restaurantId={restaurantId}
            onDelete={(r) => r.source !== "document" && setConfirmDel(r)}
          />
        )
      ) : loading ? (
        <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : rows.length === 0 ? (
        <Card className="border-dashed"><CardContent className="py-10 text-center text-sm text-muted-foreground">
          Sin gastos en este periodo. Añade uno manual o sube una factura desde la Bandeja.
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {rows.map(r => (
            <Card key={r.expense_id} data-testid={`expense-row-${r.expense_id}`}>
              <CardContent className="p-3 flex items-center gap-3">
                <div className="shrink-0 h-9 w-9 rounded-lg bg-muted border border-border flex items-center justify-center">
                  <Wallet className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-foreground truncate">{r.supplier_name || catLabels[r.category] || r.category}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-foreground border border-border">
                      {catLabels[r.category] || r.category}
                    </span>
                    {r.source === "document" && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 border border-violet-200">
                        Desde doc
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5 truncate">
                    {r.date}{r.notes ? ` · ${r.notes}` : ""}
                  </div>
                </div>
                <div className="text-sm font-semibold shrink-0">{fmtEUR(r.amount)}</div>
                {r.source !== "document" && (
                  <button
                    onClick={() => setConfirmDel(r)}
                    className="text-muted-foreground hover:text-red-700 p-1"
                    aria-label="Eliminar"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Nuevo gasto</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Fecha *</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm(f => ({ ...f, date: e.target.value }))} data-testid="expense-date" />
              </div>
              <div className="space-y-2">
                <Label>Importe (€) *</Label>
                <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm(f => ({ ...f, amount: e.target.value }))} data-testid="expense-amount" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Categoría</Label>
              <Select value={form.category} onValueChange={(v) => setForm(f => ({ ...f, category: v }))}>
                <SelectTrigger data-testid="expense-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(categories || []).map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Proveedor / Concepto</Label>
              <Input value={form.supplier_name} onChange={(e) => setForm(f => ({ ...f, supplier_name: e.target.value }))} placeholder="Ej: Endesa, Alquiler, Glovo" />
            </div>
            <div className="space-y-2">
              <Label>Notas</Label>
              <Input value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} />
            </div>

            <div className="rounded-lg border border-violet-100 bg-violet-50/40 p-3">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={!!form.is_recurring}
                  onChange={(e) => setForm(f => ({ ...f, is_recurring: e.target.checked }))}
                  className="h-4 w-4 rounded border-border text-violet-600 focus:ring-violet-500"
                  data-testid="expense-recurring-checkbox"
                />
                <Repeat className="h-3.5 w-3.5 text-violet-600" />
                <span className="text-sm font-medium text-violet-900">Es un gasto recurrente</span>
              </label>
              {form.is_recurring && (
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Frecuencia</Label>
                    <Select
                      value={form.recurring_cadence}
                      onValueChange={(v) => setForm(f => ({ ...f, recurring_cadence: v }))}
                    >
                      <SelectTrigger data-testid="expense-recurring-cadence"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CADENCE_OPTIONS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Día del mes</Label>
                    <Input
                      type="number" min={1} max={31}
                      value={form.recurring_day}
                      onChange={(e) => setForm(f => ({ ...f, recurring_day: e.target.value }))}
                      placeholder={form.date ? form.date.slice(8, 10) : "1"}
                      data-testid="expense-recurring-day"
                    />
                  </div>
                  <p className="col-span-2 text-[11px] text-violet-700/80">
                    Aparecerá en la Previsión de caja desde ya y te avisaremos si no subes
                    la próxima factura a tiempo.
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button onClick={save} disabled={saving} data-testid="save-expense-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {confirmDel && (
        <AlertDialog open={!!confirmDel} onOpenChange={() => setConfirmDel(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Eliminar gasto?</AlertDialogTitle>
              <AlertDialogDescription>
                {confirmDel.supplier_name || confirmDel.category} · {fmtEUR(confirmDel.amount)}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction className="bg-red-600 hover:bg-red-700"
                onClick={(e) => { e.preventDefault(); handleDelete(confirmDel); }}>
                Eliminar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      {pendingDup && (
        <AlertDialog open={!!pendingDup} onOpenChange={(open) => { if (!open) setPendingDup(null); }}>
          <AlertDialogContent data-testid="expense-duplicate-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>⚠️ Parece que ya tenías este gasto registrado</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-2 text-sm">
                  <p>¿Seguro que quieres añadirlo igualmente? Se podría duplicar en tu P&amp;L.</p>
                  {pendingDup.duplicate && (
                    <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-amber-900">
                      <div className="font-medium">
                        {pendingDup.duplicate.supplier_name || pendingDup.duplicate.category || "Gasto existente"}
                      </div>
                      <div className="text-xs mt-1 space-y-0.5">
                        {pendingDup.duplicate.amount != null && (
                          <div>Importe: <strong>{fmtEUR(pendingDup.duplicate.amount)}</strong></div>
                        )}
                        {pendingDup.duplicate.date && (
                          <div>Fecha: <strong>{pendingDup.duplicate.date}</strong></div>
                        )}
                        {pendingDup.duplicate.reason && (
                          <div className="italic opacity-80">{pendingDup.duplicate.reason}</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="expense-duplicate-cancel-btn">Cancelar</AlertDialogCancel>
              <AlertDialogAction
                data-testid="expense-duplicate-confirm-btn"
                className="bg-amber-600 hover:bg-amber-700"
                onClick={(e) => { e.preventDefault(); confirmAddDuplicate(); }}>
                Sí, añadir igualmente
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}
